import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Link,
  Grid,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useIpcTdAccounting, useNo3Cn56ResetBlock, useNo3Cn56ResetRecord, type IpcTdAccountingDataStructure, type IpcTdAccountingItem, type No3Cn56ResetBlockParams, type No3Cn56ResetRecordParams } from './hooks';
import Table from '../../../common/components/Table/Table';
import { No3Cn56SummaryTable, No3Cn56SummaryData } from './components/No3Cn56SummaryTable';
import { EditNo3Cn56Modal } from './components/EditNo3Cn56Modal';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import {
  MainContent,
  ReportContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton,
} from './No3Cn56Summary.styled';

interface ServiceTypeState {
  aircraftPriority: boolean;
  salNonPriority: boolean;
  surfaceNonPriority: boolean;
  surfacePriority: boolean;
}

interface AccountingTypeState {
  gross: boolean;
  net: boolean;
}

interface DirectionOption {
  id: string;
  label: string;
}



// Move mailColumns outside component and memoize it
const createMailColumns = () => [
  { accessor: 'outboundOe', Header: 'OE From' },
  { accessor: 'inboundOe', Header: 'OE To' },
  { accessor: 'rcLabel', Header: 'Receptacle Category' },
  {
    accessor: 'gross',
    Header: 'Gross Sum',
    Cell: ({ value }: { value: number }) => value?.toFixed(2),
  },
  { accessor: 'tare', Header: 'Tare Sum' },
  { accessor: 'net', Header: 'Net Kg', Cell: ({ value }: { value: number }) => value?.toFixed(2) },
  { accessor: 'receivedReceptacleStratum', Header: 'Received Receptacle Stratum' },
  { accessor: 'numberOfSamples', Header: 'Valid Samples' },
  {
    accessor: 'effectiveSamplingRateStratum',
    Header: 'Effective Sampling Rate Stratum',
    Cell: ({ value }: { value: number }) => value?.toFixed(4),
  },
  {
    accessor: 'netIpk',
    Header: 'Net IPK',
    Cell: ({ value }: { value: number }) => value?.toFixed(4),
  },
  { accessor: 'pkg', Header: 'P Kg', Cell: ({ value }: { value: number }) => value?.toFixed(2) },
  { accessor: 'gkg', Header: 'G Kg', Cell: ({ value }: { value: number }) => value?.toFixed(2) },
  { accessor: 'ekg', Header: 'E Kg', Cell: ({ value }: { value: number }) => value?.toFixed(2) },
  { accessor: 'pitems', Header: 'P Items' },
  { accessor: 'gitems', Header: 'G Items' },
  { accessor: 'eitems', Header: 'E Items' },
  { accessor: 'jitems', Header: 'J Items' },
  { accessor: 'zitems', Header: 'Z Items' },
  { accessor: 'jkg', Header: 'J Kg', Cell: ({ value }: { value: number }) => value?.toFixed(2) },
  { accessor: 'zkg', Header: 'Z Kg', Cell: ({ value }: { value: number }) => value?.toFixed(2) },
];

// Memoized Results Section Component
const ResultsSection = React.memo<{
  htmlResults: IpcTdAccountingDataStructure[];
  mailColumns: any[];
  direction: string;
  no3Cn56Data?: any; // Add this prop for NO3 & CN56 Summary data
  onEditRecord?: (row: No3Cn56SummaryData) => void;
  onResetRecord?: (row: No3Cn56SummaryData) => void;
  onResetBlock?: (groupKey: string, direction: 'inbound' | 'outbound') => void;
  onDownloadPdf?: (row: No3Cn56SummaryData) => void;
  onDownloadXls?: (row: No3Cn56SummaryData) => void;
}>(({ htmlResults, mailColumns, direction, no3Cn56Data, onEditRecord, onResetRecord, onResetBlock, onDownloadPdf, onDownloadXls }) => {
  return (
    <Box sx={{ mt: 6 }}>

      {/* NO3 & CN56 Summary Table - Show first if data exists */}
      {no3Cn56Data && (
        <No3Cn56SummaryTable 
          data={no3Cn56Data} 
          isLoading={false}
          onEditRecord={onEditRecord}
          onResetRecord={onResetRecord}
          onResetBlock={onResetBlock}
          onDownloadPdf={onDownloadPdf}
          onDownloadXls={onDownloadXls}
        />
      )}

      {/* Original Results Tables */}
      {htmlResults.map((operatorData: IpcTdAccountingDataStructure, operatorIndex: number) => (
        <OperatorSection
          key={`${operatorData.operator}-${operatorIndex}`}
          operatorData={operatorData}
          operatorIndex={operatorIndex}
          mailColumns={mailColumns}
          direction={direction}
        />
      ))}
    </Box>
  );
});

// Memoized Operator Section Component
const OperatorSection = React.memo<{
  operatorData: IpcTdAccountingDataStructure;
  operatorIndex: number;
  mailColumns: any[];
  direction: string;
}>(({ operatorData, operatorIndex, mailColumns, direction }) => {
  return (
    <Box
      sx={{
        mb: 6,
        p: 3,
        border: '1px solid #E1E8ED',
        borderRadius: '8px',
        backgroundColor: '#FAFBFC',
      }}
    >
      {/* Operator Title */}
      <Box
        sx={{
          mb: 3,
          pb: 2,
          borderBottom: '1px solid #E1E8ED',
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontWeight: 600,
            color: '#063142',
            fontSize: '20px',
            letterSpacing: '-0.25px',
          }}
        >
          Operator: {operatorData.operator}
        </Typography>
      </Box>

      {operatorData.items.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: '#FFFFFF',
            borderRadius: '6px',
            border: '1px dashed #D1D9E0',
          }}
        >
          <Typography
            sx={{
              fontStyle: 'italic',
              color: '#666',
              fontSize: '14px',
            }}
          >
            No data available for this operator
          </Typography>
        </Box>
      ) : (
        operatorData.items.map((item: IpcTdAccountingItem, itemIndex: number) => (
          <MailItemSection
            key={`${item.serviceType}-${item.accountingType}-${itemIndex}`}
            item={item}
            itemIndex={itemIndex}
            isLastItem={itemIndex === operatorData.items.length - 1}
            mailColumns={mailColumns}
            direction={direction}
          />
        ))
      )}
    </Box>
  );
});

// Memoized Mail Item Section Component
const MailItemSection = React.memo<{
  item: IpcTdAccountingItem;
  itemIndex: number;
  isLastItem: boolean;
  mailColumns: any[];
  direction: string;
}>(({ item, itemIndex, isLastItem, mailColumns, direction }) => {
  return (
    <Box
      sx={{
        mb: !isLastItem ? 4 : 0,
        p: 3,
        backgroundColor: '#FFFFFF',
        borderRadius: '6px',
        border: '1px solid #E1E8ED',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Table Header Info */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: '#F8F9FA',
          borderRadius: '4px',
          border: '1px solid #E9ECEF',
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            color: '#063142',
            fontSize: '16px',
            mb: 1,
          }}
        >
          Service Type: {item.serviceType} | Accounting Type: {item.accountingType}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: '#6C757D',
            fontSize: '12px',
            fontFamily: 'monospace',
            backgroundColor: '#FFFFFF',
            padding: '4px 8px',
            borderRadius: '3px',
            border: '1px solid #DEE2E6',
            display: 'inline-block',
          }}
        >
          📄 {item.fileName}
        </Typography>
      </Box>

      {item.rows.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: '#FFF8E1',
            borderRadius: '4px',
            border: '1px dashed #FFE082',
          }}
        >
          <Typography
            sx={{
              fontStyle: 'italic',
              color: '#F57F17',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            ⚠️ No rows available for this item
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            '& .MuiPaper-root': {
              boxShadow: 'none',
              border: '1px solid #E1E8ED',
            },
          }}
        >
          <Table
            data={item.rows}
            columns={mailColumns}
            moduleName={``}
            viewMode={true}
            disableAutoSizing={false}
            pageSize={25}
            lazyLoad={item.rows.length > 50}
            lazyLoadHeight={400}
            lazyLoadingBatchSize={25}
            rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
          />
        </Box>
      )}
    </Box>
  );
});



function No3Cn56Summary() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  const [ppoValue, setPpoValue] = useState('');
  const [ppoFilter, setPpoFilter] = useState('All');
  const [selectedYear, setSelectedYear] = useState('');

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<IpcTdAccountingDataStructure[] | null>(null);
  // Add state to track when no data is found
  const [noDataFound, setNoDataFound] = useState<boolean>(false);

  // Add state for NO3 & CN56 Summary data
  const [no3Cn56Data, setNo3Cn56Data] = useState<any>(null);

  // State for edit modal
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<No3Cn56SummaryData | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Get effective operators and cascading logic for eno1 module
  const {
    operators,
    availableYears,
    operatorsForYear,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('eno1', { selectedYear, selectedPpo: ppoValue || undefined });

  // IpcTdAccounting Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError,
  } = useIpcTdAccounting();

  // Reset block hook
  const {
    mutate: resetBlock,
    isPending: isResettingBlock,
    error: resetBlockError,
    reset: resetResetBlockError,
  } = useNo3Cn56ResetBlock();

  // Reset record hook
  const {
    mutate: resetRecord,
    isPending: isResettingRecord,
    error: resetRecordError,
    reset: resetResetRecordError,
  } = useNo3Cn56ResetRecord();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = selectedYear ? operatorsForYear : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYear, selectedYear]);

  // Generate year options with cascading logic
  const yearOptions = React.useMemo(() => {
    const yearsToShow = ppoValue ? yearsForOperator : availableYears;
    return yearsToShow.map(year => ({ value: year.toString(), label: year.toString() }));
  }, [availableYears, yearsForOperator, ppoValue]);

  // Service Type state
  const [serviceType, setServiceType] = useState<ServiceTypeState>({
    aircraftPriority: true,
    salNonPriority: true,
    surfaceNonPriority: true,
    surfacePriority: true,
  });

  // Accounting Type state
  const [accountingType, setAccountingType] = useState<AccountingTypeState>({
    gross: true,
    net: true,
  });

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'All', label: 'All' },
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/ipc-td-accounting');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = useCallback((value: string) => {
    setPpoValue(value);
  }, []);

  // Handle year selection change with cascading behavior
  const handleYearChange = useCallback((value: string) => {
    // Only clear DO selection if the currently selected DO is not available for the new year
    if (selectedYear !== value && ppoValue) {
      // Check if the current DO is available for the new year
      const relationsForNewYear = allRelationsData?.byYear?.[value];
      const isCurrentDOAvailableForNewYear = relationsForNewYear?.some(relation => 
        relation.targetDO === ppoValue &&
        relation.modules.some(module => 
          module.name === 'eno1' && module.enabled === true
        )
      );
      
      if (!isCurrentDOAvailableForNewYear) {
        setPpoValue('');
      }
    }
    setSelectedYear(value);
  }, [selectedYear, ppoValue, allRelationsData]);

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleServiceTypeChange =
    (field: keyof ServiceTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setServiceType((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleAccountingTypeChange =
    (field: keyof AccountingTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setAccountingType((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleSelectAllServiceType = () => {
    const allSelected =
      serviceType.aircraftPriority &&
      serviceType.salNonPriority &&
      serviceType.surfaceNonPriority &&
      serviceType.surfacePriority;

    if (allSelected) {
      // Deselect all
      setServiceType({
        aircraftPriority: false,
        salNonPriority: false,
        surfaceNonPriority: false,
        surfacePriority: false,
      });
    } else {
      // Select all
      setServiceType({
        aircraftPriority: true,
        salNonPriority: true,
        surfaceNonPriority: true,
        surfacePriority: true,
      });
    }
  };

  const handleSelectAllAccountingType = () => {
    const allSelected = accountingType.gross && accountingType.net;

    if (allSelected) {
      // Deselect all
      setAccountingType({
        gross: false,
        net: false,
      });
    } else {
      // Select all
      setAccountingType({
        gross: true,
        net: true,
      });
    }
  };

  const handleReset = () => {
    setPpoValue('');
    setPpoFilter('Inbound');
    setSelectedYear('');
    setServiceType({
      aircraftPriority: true,
      salNonPriority: true,
      surfaceNonPriority: true,
      surfacePriority: true,
    });
    setAccountingType({
      gross: true,
      net: true,
    });
    // Clear no data found state and results when resetting
    setNoDataFound(false);
    setHtmlResults(null);
    setNo3Cn56Data(null);
  };

  // Map checkbox states to API parameter values
  const getMailCategories = (): string[] => {
    const values: string[] = [];
    if (serviceType.aircraftPriority) values.push('A');
    if (serviceType.salNonPriority) values.push('B');
    if (serviceType.surfaceNonPriority) values.push('C');
    if (serviceType.surfacePriority) values.push('D');
    return values;
  };

  const getAccountingType = (): string => {
    // If both Net and Gross are selected, send "A" (All) instead of both "N" and "G"
    if (accountingType.gross && accountingType.net) {
      return 'A';
    } else if (accountingType.gross) {
      return 'G';
    } else if (accountingType.net) {
      return 'N';
    }
    return ''; // Default case
  };

  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    } else {
      newParams.append('ppo', 'All');
    }

    // Map direction filter to table parameter (to match NO2/NO3 format)
    newParams.append('table', ppoFilter.toLowerCase());

    // Map year if available
    if (selectedYear) {
      newParams.append('year', selectedYear);
    } else {
      newParams.append('year', 'all');
    }

    // Add custom IpcTdAccounting-specific filters
    const mailCategories = getMailCategories();
    const accountingType = getAccountingType();

    if (mailCategories.length > 0) {
      newParams.append('mailCategories', mailCategories.join(','));
    }

    if (accountingType) {
      newParams.append('accountingType', accountingType);
    }

    setSearchParams(newParams);
  };

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields
    if (!selectedYear) {
      showSnackbar('Please select a Year');
      return;
    }

    // Validate required fields
    const mailCategories = getMailCategories();
    const accountingType = getAccountingType();

    if (mailCategories.length === 0) {
      showSnackbar('Please select at least one Mail Category');
      return;
    }

    if (!accountingType) {
      showSnackbar('Please select at least one Accounting Type');
      return;
    }

    // Prepare API parameters
    const params = {
      mailCategories,
      accountingType,
      year: parseInt(selectedYear),
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      direction: ppoFilter === 'All' ? 'ALL' : ppoFilter.toUpperCase(),
      ...(ppoValue && { designatedOperator: ppoValue }),
    };

    generateReport(params, {
      onSuccess: (response) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          
          // The response.data contains the actual data for HTML format
          const responseData = (response as any).data || response;
          
          // Check for empty data
          if (!responseData || 
              (Array.isArray(responseData) && responseData.length === 0) ||
              (typeof responseData === 'object' && Object.keys(responseData).length === 0)) {
            // Set no data found state instead of clearing results
            setNoDataFound(true);
            setHtmlResults(null);
            setNo3Cn56Data(null);
            // Don't apply filters to SectionBar when no data is found
            return;
          }

          // Check if the response contains NO3 & CN56 Summary data
          // This would be in the format with inbound/outbound properties
          const responseDataAny = responseData as any;
          if (responseDataAny && typeof responseDataAny === 'object' && (responseDataAny.inbound || responseDataAny.outbound)) {
            setNo3Cn56Data(responseDataAny);
            setHtmlResults(null);
          } else if (Array.isArray(responseData)) {
            // Handle array of IpcTdAccountingDataStructure
            setNo3Cn56Data(null);
            setHtmlResults(responseData);
          } else {
            // Handle other data structures
            setNo3Cn56Data(null);
            // If responseData has a data property, use it, otherwise wrap the response in an array
            setHtmlResults(responseData.data || [responseData as any]);
          }
          
          // Clear no data found state
          setNoDataFound(false);
          
          // Apply current filters to SectionBar only when data is found
          applyFiltersToSectionBar();
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(
            `${format} Report downloaded successfully: ${response.filename || `NO3_CN56_Summary.${format.toLowerCase()}`}`
          );
        }
      },
      onError: async (error: Error) => {
        console.error('Error generating report:', error);
        
        // Handle NOT_FOUND errors for PDF/XLS exports
        if (format !== 'HTML') {
          let errorMessage = 'An error occurred while generating the report';
          let errorStatus: string | undefined;
          
          // Check if it's an axios error
          if (axios.isAxiosError(error)) {
            // For blob responses, the error data might be a Blob containing JSON
            if (error.response?.data instanceof Blob) {
              try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorMessage;
                errorStatus = errorData.status;
              } catch (parseError) {
                // If parsing fails, use default message
                console.error('Failed to parse error response:', parseError);
              }
            } else if (error.response?.data && typeof error.response.data === 'object') {
              // For non-blob responses, check the data directly
              const errorData = error.response.data as { message?: string; status?: string };
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            }
            
            // Check status code as fallback
            if (!errorStatus && error.response?.status === 404) {
              errorStatus = 'NOT_FOUND';
            }
          }
          
          // Show snackbar with appropriate message
          if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No NO3 & CN56 Summary reports available') || errorMessage.includes('No NO3') || errorMessage.includes('No CN56')) {
            showSnackbar(
              errorMessage || 'No NO3 & CN56 Summary reports available for the selected criteria',
              'warning'
            );
          } else {
            showSnackbar(errorMessage, 'error');
          }
        } else {
          // For HTML format, show error snackbar
          showSnackbar(
            error instanceof Error ? error.message : 'Failed to generate report. Please try again.',
            'error'
          );
        }
      },
    });
  };

  // Memoize mailColumns to prevent recreation on every render
  const mailColumns = useMemo(() => createMailColumns(), []);

  // Use ref to track if we've already synced URL parameters
  const hasSyncedUrlParams = useRef(false);

  // Initial URL parameter sync when page loads
  useEffect(() => {
    // Only sync once and when yearOptions are available
    if (hasSyncedUrlParams.current || yearOptions.length === 0) {
      return;
    }

    const ppoParam = searchParams.get('ppo');
    const yearParam = searchParams.get('year');
    const tableParam = searchParams.get('table');

    // Sync PPO parameter
    if (ppoParam && ppoParam !== 'All') {
      setPpoValue(ppoParam);
    }

    // Sync direction filter from table parameter
    if (tableParam) {
      setPpoFilter(tableParam === 'inbound' ? 'Inbound' : tableParam === 'outbound' ? 'Outbound' : 'All');
    }

    // Sync year parameter
    if (yearParam) {
      setSelectedYear(yearParam);
    }

    // Mark as synced
    hasSyncedUrlParams.current = true;
  }, [searchParams, yearOptions]);

  // Sync URL params with page state when SectionBar filters change (only when results exist)
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');
    const yearParam = searchParams.get('year');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults) {
      if (ppoParam && ppoParam !== 'All' && ppoParam !== ppoValue) {
        setPpoValue(ppoParam);
      }

      if (yearParam && yearParam !== 'all' && yearParam !== selectedYear) {
        setSelectedYear(yearParam);
      }
    }
  }, [searchParams, htmlResults, ppoValue, selectedYear]);

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    setNo3Cn56Data(null);
    setNoDataFound(false);
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Context menu handlers for NO3 & CN56 table
  const handleEditRecord = useCallback((row: No3Cn56SummaryData) => {
    setSelectedRowData(row);
    setEditModalOpen(true);
  }, []);

  const handleResetRecord = useCallback((row: No3Cn56SummaryData) => {
    // Prepare the reset record parameters
    const resetParams: No3Cn56ResetRecordParams = {
      sendingPpo: row.sendingPpo,
      receivingPpo: row.receivingPpo,
      year: parseInt(row.year),
      accountingType: row.accountingType,
      mailCategory: row.mailCategory,
      rowType: (row.rowIndex || 0) + 1, // Use rowIndex + 1 as rowType (row index + 1)
    };

    // Call the reset record mutation
    resetRecord(resetParams, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: `Successfully reset record for ${row.sendingPpo} - ${row.receivingPpo}`,
          severity: 'success',
        });
        
        // Refresh the data to show the updated values
        if (no3Cn56Data) {
          const currentParams = {
            mailCategories: getMailCategories(),
            accountingType: getAccountingType(),
            year: parseInt(selectedYear),
            formatType: 'html' as const,
            direction: ppoFilter === 'All' ? 'ALL' : ppoFilter.toUpperCase(),
            designatedOperator: ppoValue || undefined,
          };
          
          generateReport(currentParams, {
            onSuccess: (response) => {
              const responseData = (response as any).data || response;
              if (responseData && typeof responseData === 'object') {
                const responseDataAny = responseData as any;
                if (responseDataAny.inbound || responseDataAny.outbound) {
                  setNo3Cn56Data(responseDataAny);
                  setHtmlResults(null);
                } else if (Array.isArray(responseDataAny)) {
                  setNo3Cn56Data(null);
                  setHtmlResults(responseDataAny);
                } else if (responseDataAny.data) {
                  setNo3Cn56Data(null);
                  setHtmlResults(responseDataAny.data || [responseDataAny as any]);
                } else {
                  setNo3Cn56Data(null);
                  setHtmlResults([responseDataAny]);
                }
              }
            },
            onError: (error) => {
              console.error('Failed to refresh data after reset record:', error);
              setSnackbar({
                open: true,
                message: 'Record reset but failed to refresh data',
                severity: 'warning',
              });
            }
          });
        }
      },
      onError: (error) => {
        console.error('Error resetting record:', error);
        setSnackbar({
          open: true,
          message: `Failed to reset record: ${error.message || 'Unknown error'}`,
          severity: 'error',
        });
      },
    });
  }, [resetRecord, no3Cn56Data, getMailCategories, getAccountingType, selectedYear, ppoFilter, ppoValue, generateReport]);

  const handleResetBlock = useCallback((groupKey: string, direction: 'inbound' | 'outbound') => {
    // The groupKey is the key from the grouped data structure
    // We need to extract sendingPpo and receivingPpo from the current data
    console.log('no3Cn56Data:', groupKey);
    if (!no3Cn56Data) {
      setSnackbar({
        open: true,
        message: 'No data available for reset block operation',
        severity: 'error',
      });
      return;
    }

    // Find the first row in the group to get sendingPpo, receivingPpo, mailCategory, and accountingType
    let sendingPpo = '';
    let receivingPpo = '';
    let mailCategory = '';
    let rowAccountingType = '';
    
    // Handle nested structure with no3Cn56Summary
    if (direction === 'inbound' && no3Cn56Data.inbound) {
      // Check for nested structure first
      const dataSource = no3Cn56Data.inbound.no3Cn56Summary || no3Cn56Data.inbound;
      const group = dataSource[groupKey];
      
      if (Array.isArray(group) && group.length > 0) {
        sendingPpo = group[0].sendingPpo || '';
        receivingPpo = group[0].receivingPpo || '';
        mailCategory = group[0].mailCategory || '';
        rowAccountingType = group[0].accountingType || '';
      }
    } else if (direction === 'outbound' && no3Cn56Data.outbound) {
      // Check for nested structure first
      const dataSource = no3Cn56Data.outbound.no3Cn56Summary || no3Cn56Data.outbound;
      const group = dataSource[groupKey];
      
      if (Array.isArray(group) && group.length > 0) {
        sendingPpo = group[0].sendingPpo || '';
        receivingPpo = group[0].receivingPpo || '';
        mailCategory = group[0].mailCategory || '';
        rowAccountingType = group[0].accountingType || '';
      }
    }
    
    if (!sendingPpo || !receivingPpo || !rowAccountingType) {
      setSnackbar({
        open: true,
        message: 'Could not determine required data (PPOs or accounting type) for reset block operation',
        severity: 'error',
      });
      return;
    }

    // Prepare the reset block parameters
    const resetParams: No3Cn56ResetBlockParams = {
      sendingPpo,
      receivingPpo,
      year: parseInt(selectedYear),
      accountingType: rowAccountingType, // Use accounting type from row data only
      mailCategory: mailCategory || getMailCategories()[0], // Use mailCategory from row data, fallback to first selected category
    };

    // Call the reset block mutation
    resetBlock(resetParams, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: `Successfully reset ${direction} block for ${sendingPpo} - ${receivingPpo}`,
          severity: 'success',
        });
        
        // Refresh the data to show the updated values
        if (no3Cn56Data) {
          const currentParams = {
            mailCategories: getMailCategories(),
            accountingType: getAccountingType(),
            year: parseInt(selectedYear),
            formatType: 'html' as const,
            direction: ppoFilter === 'All' ? 'ALL' : ppoFilter.toUpperCase(),
            designatedOperator: ppoValue || undefined,
          };
          
          generateReport(currentParams, {
            onSuccess: (response) => {
              const responseData = (response as any).data || response;
              if (responseData && typeof responseData === 'object') {
                const responseDataAny = responseData as any;
                if (responseDataAny.inbound || responseDataAny.outbound) {
                  setNo3Cn56Data(responseDataAny);
                  setHtmlResults(null);
                } else if (Array.isArray(responseDataAny)) {
                  setNo3Cn56Data(null);
                  setHtmlResults(responseDataAny);
                } else if (responseDataAny.data) {
                  setNo3Cn56Data(null);
                  setHtmlResults(responseDataAny.data || [responseDataAny as any]);
                } else {
                  setNo3Cn56Data(null);
                  setHtmlResults([responseDataAny]);
                }
              }
            },
            onError: (error) => {
              console.error('Failed to refresh data after reset block:', error);
              setSnackbar({
                open: true,
                message: 'Block reset but failed to refresh data',
                severity: 'warning',
              });
            }
          });
        }
      },
      onError: (error) => {
        console.error('Error resetting block:', error);
        setSnackbar({
          open: true,
          message: `Failed to reset ${direction} block: ${error.message || 'Unknown error'}`,
          severity: 'error',
        });
      },
    });
  }, [resetBlock, selectedYear, getAccountingType, getMailCategories, no3Cn56Data, generateReport, ppoFilter, ppoValue]);

  const handleDownloadPdf = useCallback((row: No3Cn56SummaryData) => {
    // Prepare API parameters for PDF download
    const params = {
      mailCategories: getMailCategories(),
      accountingType: getAccountingType(),
      year: parseInt(selectedYear),
      formatType: 'pdf' as const,
      direction: ppoFilter === 'All' ? 'ALL' : ppoFilter.toUpperCase(),
      designatedOperator: ppoValue || undefined,
    };

    generateReport(params, {
      onSuccess: (response) => {
        setSnackbar({
          open: true,
          message: `PDF downloaded successfully: ${response.filename || 'NO3_CN56_Summary.pdf'}`,
          severity: 'success',
        });
      },
      onError: async (error: Error) => {
        console.error('Error downloading PDF:', error);
        
        let errorMessage = 'Failed to download PDF. Please try again.';
        let errorStatus: string | undefined;
        
        // Check if it's an axios error
        if (axios.isAxiosError(error)) {
          // For blob responses, the error data might be a Blob containing JSON
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            } catch (parseError) {
              // If parsing fails, use default message
              console.error('Failed to parse error response:', parseError);
            }
          } else if (error.response?.data && typeof error.response.data === 'object') {
            // For non-blob responses, check the data directly
            const errorData = error.response.data as { message?: string; status?: string };
            errorMessage = errorData.message || errorMessage;
            errorStatus = errorData.status;
          }
          
          // Check status code as fallback
          if (!errorStatus && error.response?.status === 404) {
            errorStatus = 'NOT_FOUND';
          }
        }
        
        // Show snackbar with appropriate message
        if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No NO3 & CN56 Summary reports available') || errorMessage.includes('No NO3') || errorMessage.includes('No CN56')) {
          setSnackbar({
            open: true,
            message: errorMessage || 'No NO3 & CN56 Summary reports available for the selected criteria',
            severity: 'warning',
          });
        } else {
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        }
      },
    });
  }, [generateReport, getMailCategories, getAccountingType, selectedYear, ppoFilter, ppoValue]);

  const handleDownloadXls = useCallback((row: No3Cn56SummaryData) => {
    // Prepare API parameters for XLS download
    const params = {
      mailCategories: getMailCategories(),
      accountingType: getAccountingType(),
      year: parseInt(selectedYear),
      formatType: 'xls' as const,
      direction: ppoFilter === 'All' ? 'ALL' : ppoFilter.toUpperCase(),
      designatedOperator: ppoValue || undefined,
    };

    generateReport(params, {
      onSuccess: (response) => {
        setSnackbar({
          open: true,
          message: `XLS downloaded successfully: ${response.filename || 'NO3_CN56_Summary.xls'}`,
          severity: 'success',
        });
      },
      onError: async (error: Error) => {
        console.error('Error downloading XLS:', error);
        
        let errorMessage = 'Failed to download XLS. Please try again.';
        let errorStatus: string | undefined;
        
        // Check if it's an axios error
        if (axios.isAxiosError(error)) {
          // For blob responses, the error data might be a Blob containing JSON
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            } catch (parseError) {
              // If parsing fails, use default message
              console.error('Failed to parse error response:', parseError);
            }
          } else if (error.response?.data && typeof error.response.data === 'object') {
            // For non-blob responses, check the data directly
            const errorData = error.response.data as { message?: string; status?: string };
            errorMessage = errorData.message || errorMessage;
            errorStatus = errorData.status;
          }
          
          // Check status code as fallback
          if (!errorStatus && error.response?.status === 404) {
            errorStatus = 'NOT_FOUND';
          }
        }
        
        // Show snackbar with appropriate message
        if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No NO3 & CN56 Summary reports available') || errorMessage.includes('No NO3') || errorMessage.includes('No CN56')) {
          setSnackbar({
            open: true,
            message: errorMessage || 'No NO3 & CN56 Summary reports available for the selected criteria',
            severity: 'warning',
          });
        } else {
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        }
      },
    });
  }, [generateReport, getMailCategories, getAccountingType, selectedYear, ppoFilter, ppoValue]);

  const handleModalClose = useCallback(() => {
    setEditModalOpen(false);
    setSelectedRowData(null);
  }, []);

  const handleModalSave = useCallback((updatedData: No3Cn56SummaryData) => {
    console.log('handleModalSave called with updatedData:', updatedData); // Debug log
    
    // Close the modal first
    setEditModalOpen(false);
    setSelectedRowData(null);
    
    console.log('Modal closed, now refreshing data...'); // Debug log
    
    // Always trigger a refetch of the data to get the updated values from the backend
    // Re-generate the report with the same parameters to get fresh data
    const currentParams = {
      mailCategories: getMailCategories(),
      accountingType: getAccountingType(),
      year: parseInt(selectedYear),
      formatType: 'html' as const,
      direction: ppoFilter === 'All' ? 'ALL' : ppoFilter.toUpperCase(),
      designatedOperator: ppoValue || undefined,
    };
    
    console.log('Generating report with params:', currentParams); // Debug log
    
    generateReport(currentParams, {
      onSuccess: (response) => {
        console.log('Report generated successfully:', response); // Debug log
        // Update the data with fresh data from backend
        const responseData = (response as any).data || response;
        if (responseData && typeof responseData === 'object') {
          const responseDataAny = responseData as any;
          if (responseDataAny.inbound || responseDataAny.outbound) {
            // This is NO3 & CN56 Summary data
            setNo3Cn56Data(responseDataAny);
            setHtmlResults(null);
            console.log('Updated NO3 & CN56 data'); // Debug log
          } else if (Array.isArray(responseDataAny)) {
            // This is regular HTML results
            setNo3Cn56Data(null);
            setHtmlResults(responseDataAny);
            console.log('Updated HTML results'); // Debug log
          } else if (responseDataAny.data) {
            // This has a data property
            setNo3Cn56Data(null);
            setHtmlResults(responseDataAny.data || [responseDataAny as any]);
            console.log('Updated data with data property'); // Debug log
          } else {
            // Fallback
            setNo3Cn56Data(null);
            setHtmlResults([responseDataAny]);
            console.log('Updated with fallback'); // Debug log
          }
        }
      },
      onError: (error) => {
        console.error('Failed to refresh data after edit:', error);
        setSnackbar({
          open: true,
          message: 'Record updated but failed to refresh data',
          severity: 'warning',
        });
      }
    });
  }, [generateReport, getMailCategories, getAccountingType, selectedYear, ppoFilter, ppoValue]);

  // Helper functions to determine button text
  const getServiceTypeButtonText = () => {
    const allSelected =
      serviceType.aircraftPriority &&
      serviceType.salNonPriority &&
      serviceType.surfaceNonPriority &&
      serviceType.surfacePriority;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getAccountingTypeButtonText = () => {
    const allSelected = accountingType.gross && accountingType.net;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarServiceTypesChange = useCallback((values: string[]) => {
    setServiceType({
      aircraftPriority: values.includes('A'),
      salNonPriority: values.includes('B'),
      surfaceNonPriority: values.includes('C'),
      surfacePriority: values.includes('D'),
    });
  }, []);

  const handleSectionBarAccountingTypesChange = useCallback((values: string[]) => {
    // Handle individual G/N selections only (no "A" option in SectionBar)
    setAccountingType({
      gross: values.includes('G'),
      net: values.includes('N'),
    });
  }, []);

  const handleSectionBarYearChange = useCallback((year: string) => {
    setSelectedYear(year);
  }, []);

  // Handler function for SectionBar direction change
  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setPpoFilter(direction);
  }, []);

  // Handler function for SectionBar PPO change
  const handleSectionBarPpoChange = useCallback((ppo: string) => {
    // Map SectionBar PPO values to NO3 & CN56 Summary values
    if (ppo === 'All') {
      handleDOChange('');
    } else {
      handleDOChange(ppo);
    }
  }, [handleDOChange]);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    // Only show SectionBar custom filters when we have actual results (not when no data found)
    if ((!htmlResults && !no3Cn56Data) || noDataFound) return undefined;

    return {
      serviceTypes: getMailCategories(),
      accountingTypes: (() => {
        const types: string[] = [];
        if (accountingType.gross) types.push('G');
        if (accountingType.net) types.push('N');
        return types;
      })(), // Pass individual G/N values instead of combined "A"
      year: selectedYear,
      direction: ppoFilter,
      ppoOptions: ppoOptions.map(option => ({
        id: option.id,
        name: option.label,
        disabled: false
      })), // Pass cascaded PPO options in correct format
      ppo: ppoValue || 'All', // Pass current PPO value
      isPpoLoading: isPpoLoading, // Pass PPO loading state
      yearOptions: yearOptions, // Pass cascaded year options
      onServiceTypesChange: handleSectionBarServiceTypesChange,
      onAccountingTypesChange: handleSectionBarAccountingTypesChange,
      onYearChange: handleSectionBarYearChange,
      onDirectionChange: handleSectionBarDirectionChange,
      onPpoChange: handleSectionBarPpoChange, // Add PPO change handler
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    no3Cn56Data,
    noDataFound,
    serviceType,
    accountingType,
    selectedYear,
    ppoFilter,
    ppoOptions,
    ppoValue,
    isPpoLoading,
    yearOptions,
    handleSectionBarServiceTypesChange,
    handleSectionBarAccountingTypesChange,
    handleSectionBarYearChange,
    handleSectionBarDirectionChange,
    handleSectionBarPpoChange,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='no3-cn56-summary-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='NO3 & CN56 Summary'
            subtitle='IPC TD Accounting'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            module="eno1"
            showFilters={{
              ppo: !!(htmlResults || no3Cn56Data) && !noDataFound,
              customIpcTdAccounting: !!(htmlResults || no3Cn56Data) && !noDataFound,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="no3-cn56-summary-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='no3-cn56-summary-main-content'>
            <ReportContainer data-testid="no3-cn56-summary-content">
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid="no3-cn56-summary-header"
              >
                <Typography variant='h4' sx={{ fontWeight: 600, color: '#063142' }} data-testid="no3-cn56-summary-title">
                  NO3 & CN56 Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="no3-cn56-summary-header-actions">
                  {(htmlResults || no3Cn56Data || noDataFound) && (
                    <ClearResultsButton variant='outlined' onClick={clearResults} data-testid="no3-cn56-summary-clear-results-btn">
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Error Alert */}
              {reportError && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => resetReportError()}>
                  {reportError.message || 'An error occurred while generating the report'}
                </Alert>
              )}
              
              {/* Reset Block Error Alert */}
              {resetBlockError && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => resetResetBlockError()}>
                  {resetBlockError.message || 'An error occurred while resetting the block'}
                </Alert>
              )}
              
              {/* Reset Record Error Alert */}
              {resetRecordError && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => resetResetRecordError()}>
                  {resetRecordError.message || 'An error occurred while resetting the record'}
                </Alert>
              )}

              {/* Main Filter Section - Hide when results exist (but show when no data found) */}
              {!htmlResults && !no3Cn56Data && (
                <>
                  {/* PPO Section */}
                  <FilterSection>
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                              renderValue={(selected) => {
                                if (!selected) {
                                  return <span style={{ color: '#999' }}>Select DO</span>;
                                }
                                return selected;
                              }}
                            >
                              {isPpoLoading && (
                                <MenuItem value='' disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                </MenuItem>
                              )}
                              {!isPpoLoading &&
                                !ppoError &&
                                (selectedYear ? operatorsForYear : operators).map((targetDO) => (
                                  <MenuItem key={targetDO} value={targetDO}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                      }}
                                    >
                                      <span>{targetDO}</span>
                                     
                                    </Box>
                                  </MenuItem>
                                ))}
                              {ppoError && <MenuItem disabled>Error loading PPOs</MenuItem>}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl>
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px',
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        mb: 2,
                      }}
                    />

                    {/* Year Selector with title */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          Year: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={selectedYear}
                              onChange={(e) => handleYearChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value=''>Select</MenuItem>
                              {(ppoValue ? yearsForOperator.map(year => year.toString()) : yearOptions.map(opt => opt.value)).map((year) => (
                                <MenuItem key={year} value={year}>
                                  {year}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                  />

                  {/* Main Content Grid - without borders, with dividers */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      width: '100%',
                    }}
                  >
                    {/* Mail Category */}
                    <Box
                      sx={{
                        flex: { xs: 'none', md: '1 1 0' },
                        padding: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <SectionTitle>Mail Category</SectionTitle>
                        <Link
                          component='button'
                          onClick={handleSelectAllServiceType}
                          sx={{
                            color: '#00ACE7',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none',
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC',
                            },
                          }}
                        >
                          {getServiceTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={serviceType.aircraftPriority}
                          onChange={handleServiceTypeChange('aircraftPriority')}
                          label='A - Airmail Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.salNonPriority}
                          onChange={handleServiceTypeChange('salNonPriority')}
                          label='B - SAL Non-Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.surfaceNonPriority}
                          onChange={handleServiceTypeChange('surfaceNonPriority')}
                          label='C - Surface Non-Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.surfacePriority}
                          onChange={handleServiceTypeChange('surfacePriority')}
                          label='D - Surface Priority'
                        />
                      </CheckboxGroup>
                    </Box>

                    {/* Vertical Divider */}
                    <Box
                      sx={{
                        width: '1px',
                        backgroundColor: '#E1E8ED',
                        display: { xs: 'none', md: 'block' },
                      }}
                    />

                    {/* Horizontal Divider for mobile */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        my: 2,
                        display: { xs: 'block', md: 'none' },
                      }}
                    />

                    {/* Accounting Type */}
                    <Box
                      sx={{
                        flex: { xs: 'none', md: '1 1 0' },
                        padding: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <SectionTitle>Accounting Type</SectionTitle>
                        <Link
                          component='button'
                          onClick={handleSelectAllAccountingType}
                          sx={{
                            color: '#00ACE7',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none',
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC',
                            },
                          }}
                        >
                          {getAccountingTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={accountingType.net}
                          onChange={handleAccountingTypeChange('net')}
                          label='Net'
                        />
                        <CustomCheckbox
                          checked={accountingType.gross}
                          onChange={handleAccountingTypeChange('gross')}
                          label='Gross'
                        />
                      </CheckboxGroup>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                    <ResetButton variant='contained' onClick={handleReset}>
                      RESET
                    </ResetButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('HTML')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('PDF')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('XLS')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                    </ExportButton>
                  </ButtonContainer>
                </>
              )}

              {/* No Data Found Section */}
              {noDataFound && (
                <NoDataFound
                  title="No Data Available"
                  message="No NO3 & CN56 Summary report data was found for the selected criteria. Please try adjusting your filters or selecting a different date range."
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                />
              )}

              {/* Results Section - Show tables directly under filters */}
              {(htmlResults || no3Cn56Data) && !noDataFound && (
                <ResultsSection 
                  htmlResults={htmlResults || []} 
                  mailColumns={mailColumns} 
                  direction={ppoFilter}
                  no3Cn56Data={no3Cn56Data}
                  onEditRecord={handleEditRecord}
                  onResetRecord={handleResetRecord}
                  onResetBlock={handleResetBlock}
                  onDownloadPdf={handleDownloadPdf}
                  onDownloadXls={handleDownloadXls}
                />
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='no3-cn56-summary-snackbar'
      />

             {/* Edit Modal */}
       <EditNo3Cn56Modal
         open={editModalOpen}
         onClose={handleModalClose}
         data={selectedRowData}
         onSave={handleModalSave}
         onShowSnackbar={showSnackbar}
         fullData={no3Cn56Data}
       />
    </Box>
  );
}

export default No3Cn56Summary; 